import React, { useCallback, useMemo } from "react";

import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import { MenuProps } from "antd/es/menu";
import { MenuInfo } from "rc-menu/lib/interface";
import { useIntl } from "react-intl";

import FieldVisibility, { FieldTeamVisibility } from "@mapmycustomers/shared/enum/FieldVisibility";
import SchemaFieldCategory from "@mapmycustomers/shared/enum/SchemaFieldCategory";
import SchemaField from "@mapmycustomers/shared/types/schema/SchemaField";

import { getFieldTeamVisibilityName } from "@app/util/ui";

import styles from "./VisibilitySelectField.module.scss";

interface Props {
  calculatedField?: boolean;
  disabledFieldVisibilities?: FieldTeamVisibility[];
  field?: SchemaField;
  onChange?: (visibility: FieldTeamVisibility) => void;
  phiEnabled?: boolean;
  value?: FieldTeamVisibility;
}

const VisibilitySelectField: React.FC<Props> = ({
  calculatedField,
  disabledFieldVisibilities,
  field,
  onChange,
  phiEnabled,
  value,
}) => {
  const intl = useIntl();

  const visibilityOptions = useMemo(
    () =>
      [
        ...(calculatedField ? [] : [FieldVisibility.FULL_ACCESS]),
        FieldVisibility.PUBLIC,
        ...(field?.category !== SchemaFieldCategory.SYSTEM_REQUIRED || phiEnabled
          ? [FieldVisibility.PRIVATE]
          : []),
      ] as FieldTeamVisibility[],
    [calculatedField, field?.category, phiEnabled]
  );

  const handleClick = useCallback(
    ({ key }: MenuInfo) => {
      onChange?.(key as FieldTeamVisibility);
    },
    [onChange]
  );

  const menuProps: MenuProps = useMemo(
    () => ({
      activeKey: value,
      items: visibilityOptions.map((visibility) => ({
        disabled: disabledFieldVisibilities?.includes(visibility),
        key: visibility,
        label: getFieldTeamVisibilityName(intl, visibility, phiEnabled),
        onClick: handleClick,
      })),
    }),
    [disabledFieldVisibilities, handleClick, intl, phiEnabled, value, visibilityOptions]
  );

  return (
    <Dropdown className={styles.container} menu={menuProps} trigger={["click"]}>
      <div>
        {getFieldTeamVisibilityName(
          intl,
          value ??
            visibilityOptions
              .filter((visibility) => !disabledFieldVisibilities?.includes(visibility))
              .at(-1)!,
          phiEnabled
        )}
        <FontAwesomeIcon icon={faChevronDown} />
      </div>
    </Dropdown>
  );
};

export default VisibilitySelectField;
